import Pagination, { PaginationPresentationProps } from "@DSG/MobiusOverrides/Pagination/Pagination";
import colorPallete, { bodyFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => { 
    const currentPageState = getCurrentPage(state);
    const pages = getDataView(state.data.pages, { parentNodeId: currentPageState.nodeId });
    const session = getSession(state);
    const defaultLanguages = state.context.website.languages!.languages!.filter(o => o.isDefault);
    const defaultLanguageId = defaultLanguages.length > 0 ? defaultLanguages[0].id : emptyGuid;
    const currentLanguageId = session.language!.id;
    
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: currentPageState.nodeId,
        location: getLocation(state),
        websiteName: state.context.website.name,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        pages,
        links,
        currentPageState,
        parameter: state.pages.newsList.getNewsPagesParameters[currentPageState.nodeId] ?? {
            parentNodeId: currentPageState.nodeId,
        },
        defaultLanguageId,
        currentLanguageId,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
}

export const newsListViewStyles: NewsListViewStyles = {
    listWrapper: {
        css: css`
            padding-top: 4px;
            .pagination .pagination-text {
                display: none !important;
            }
            [class*="PerPageSelect"] {
                color: ${colorPallete.black};
                margin-bottom: 24px;
                [data-test-selector="pagination_resultsPerPage"] {
                    min-height: 32px;
                    height: 32px;
                    &,
                    &:hover,
                    &:focus {
                        padding: 0 32px 0 16px;
                    }
                    & + [class*="IconWrapper"] {
                        height: 32px;
                        padding-top: 11px;
                    }
                }
                label {
                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize}/${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                    padding-top: 0;
                    min-height: 32px;
                    display: flex;
                    align-items: center;
                    width: auto;
                }
                [class*="FormFieldLabel"] + [class*="FormInputWrapper"] {
                    max-width: 72px;
                    margin-top: 0;
                }
                [class*="FormFieldStyle"] {
                    display: flex;
                    align-items: center;
                    padding-right: 0;
                    justify-content: center;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding-top: 8px;
                    `,
                    css`
                        [class*="PaginationStyle"].pagination {
                            align-items: flex-start;
                        }
                        [class*="PerPageSelect"] {
                            margin-bottom: 0;
                            [class*="FormFieldStyle"] {
                                justify-content: flex-end;
                                margin-right: 28px;
                            }
                            [data-test-selector="pagination_resultsPerPage"] {
                                min-height: 40px;
                                height: 40px;
                                & + [class*="IconWrapper"] {
                                    height: 40px;
                                    padding-top: 11px;
                                }
                            }
                            label {
                                min-height: 40px;
                            }
                            [class*="FormFieldLabel"] + [class*="FormInputWrapper"] {
                                max-width: 76px;
                            }
                        }
                    `,
                ],
                "min",
            )}
        `
    },
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
            [class*="StyledClickable"] {
                &, &:hover, &:focus, &:active {
                    text-decoration: none;
                    span[class*="StyledTypography"] {
                        color: ${colorPallete.grey800} !important;
                        text-decoration: none;
                    }
                }
            }
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 24px;
            border-top: 1px solid ${colorPallete.grey300};
            padding-top: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-bottom: 24px;
                        padding-top: 24px;
                    `,
                ],
                "min",
            )}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: none;
                padding-top: 24px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 0 !important;
                            margin-bottom: 0;
                        `,
                    ],
                    "min",
                )}
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    searchTimeoutId: number | undefined;

    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentWillUnmount() {
        if (typeof this.searchTimeoutId === "number") {
            clearTimeout(this.searchTimeoutId);
        }
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }
    
    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
        this.scrollToTop()
    };
    
    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };
    
    scrollToTop = () => {
        if (typeof this.searchTimeoutId === "number") {
            clearTimeout(this.searchTimeoutId);
        }
        this.searchTimeoutId = setTimeout(() => {
            const contentWrapper = document.querySelector(".cms-content");
            if (contentWrapper) {
                window.scrollTo(0, 0);
            }
        }, 300);
    }

    getQueryStrings(newPage: number | undefined, totalItems: any) {
        let prevPageUri; let nextPageUri = "";
        if (newPage === undefined)
            {
                    prevPageUri="";
                    nextPageUri = `?page=2`;
            } 
            else if(newPage && newPage === totalItems)
                    {
                        prevPageUri=`?page=${newPage - 1}`;
                        nextPageUri = "";
                    }
                    else if(newPage && totalItems === 1)
                        {
                            prevPageUri="";
                            nextPageUri = "";
                        }
                        else if(newPage && newPage === 1)
                            {
                                prevPageUri="";
                                nextPageUri = `?page=${newPage + 1}`;
                            }
                        else if(newPage)
                            {
                                prevPageUri=`?page=${newPage - 1}`;
                                nextPageUri = `?page=${newPage + 1}`;
                            }
            return{prevPageUri,nextPageUri};
    }

    setMetadata(newPage: number | undefined, totalItems: any) {
        
        const languageKey = this.props.currentLanguageId ?? this.props.defaultLanguageId;
        const websiteName = this.props.websiteName;
        const websiteSettings = this.props.websiteSettings;
        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            primaryImagePath,
            alternateLanguageUrls,
        } = this.props.currentPageState.translatableFields;

        const { prevPageUri, nextPageUri } = this.getQueryStrings(newPage,totalItems);

        setPageMetadata(
            {
                metaDescription: metaDescription[languageKey],
                metaKeywords:metaKeywords[languageKey],
                openGraphImage: openGraphImage[languageKey] || primaryImagePath,
                openGraphTitle:openGraphTitle[languageKey],
                openGraphUrl:openGraphUrl[languageKey],
                title:title[languageKey],
                currentPath: this.props.location.pathname,
                alternateLanguageUrls: alternateLanguageUrls ?? undefined,
                websiteName,
                prevQueryString:prevPageUri,
                nextQueryString:nextPageUri,
            },
            websiteSettings,
        );
    }
    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
            pages: { value: pages },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
        const totalItems = pages?.length;
        const recordsPerPage = this.props.parameter.pageSize ?? this.props.fields.defaultPageSize;
        if(totalItems)
        {
            const totalPageCount = Math.ceil(totalItems / recordsPerPage)
            this.setMetadata(newPage,totalPageCount);
        }
       
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const totalItems = pages.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        return (
            <StyledWrapper {...styles.listWrapper}>
                {sortedAndPagedPages.map((page: PageProps) => (
                    <StyledWrapper {...styles.newsWrapper} key={page.id}>
                        <Typography variant="h5" {...styles.titleText}>
                            <Link href={links[page.nodeId]?.url} data-test-selector="newslist_pagetitle">
                                {page.fields["title"]}
                            </Link>
                        </Typography>
                        <StyledWrapper {...styles.dateAndAuthorWrapper}>
                            {page.fields["author"] && (
                                <Typography {...styles.authorText} data-test-selector="newslist_author">
                                    {page.fields["author"]}
                                </Typography>
                            )}
                            {page.fields["publishDate"] && (
                                <Typography as="p" {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                    <LocalizedDateTime
                                        dateTime={new Date(page.fields["publishDate"])}
                                        options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                                    />
                                </Typography>
                            )}
                        </StyledWrapper>
                        {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                            <StyledWrapper {...styles.summaryWrapper} data-test-selector="newslist_content">
                                {parse(
                                    page.fields["newsSummary"] || truncate(page.fields["newsContent"], { length: 500 }),
                                    parserOptions,
                                )}
                            </StyledWrapper>
                        )}
                        <Link
                            href={links[page.nodeId]?.url}
                            {...styles.readMoreLink}
                            className="text-link text-link-primary text-link-active"
                            data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                        >
                            {translate("Read more")}
                        </Link>
                    </StyledWrapper>
                ))}
                {totalItems > defaultPageSize && (
                    <Pagination
                        resultsCount={totalItems}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageOptions={[
                            defaultPageSize,
                            defaultPageSize * 2,
                            // defaultPageSize * 3,
                            // defaultPageSize * 4,
                        ]}
                        onChangePage={this.changePage}
                        onChangeResultsPerPage={this.changeResultsPerPage}
                        pageSizeCookie="NewsList-PageSize"
                        displayResultsPerPage={true}
                        {...styles.pagination}
                    />
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
